/**
 * The per-NPC read screen (§12): what your character thinks they know.
 * Honestly labeled, so it never feels like a cheat when it turns out
 * wrong. The wording follows the sim's confidence number; the number
 * itself is never shown.
 */

import { type Action, factsBetween, formatTime, type NpcId, type WorldState } from '@sim/index.ts';

import { describeFamiliarity, describeRead } from './hedge.ts';
import { describeProposition, describeSubstance } from './say.ts';

interface Props {
  readonly state: WorldState;
  readonly npc: NpcId;
  readonly onClose: () => void;
  /** Things you could start with this person right now (§17), decided by the sim. */
  readonly starters?: readonly { readonly label: string; readonly action: Action }[];
  readonly onAct?: (action: Action) => void;
}

export function ReadScreen({ state, npc: id, onClose, starters = [], onAct }: Props) {
  const npc = state.npcs[id];
  if (!npc) return null;
  const known = npc.read.acquaintance === 'known';
  const first = npc.name.split(' ')[0];

  const facts = factsBetween(state, 'player', id);
  const debts = facts.filter((f) => f.kind === 'obligation' && !f.settled);
  const claims = facts.filter((f) => f.kind === 'claim' && f.by === id);
  const meetings = Object.values(state.appointments).filter(
    (a) => a.status.kind === 'pending' && a.participants.includes(id) && a.participants.includes('player'),
  );

  return (
    <section className="read" aria-label={`What you make of ${known ? npc.name : 'this person'}`}>
      <header>
        <h1>{known ? npc.name : 'Someone'}</h1>
        <button type="button" className="close" onClick={onClose}>
          Back to the map
        </button>
      </header>
      <p className="role">{known ? npc.role : describeFamiliarity(npc.read)}</p>

      <h2>What you make of {known ? first : 'them'}</h2>
      {known && <p>{describeFamiliarity(npc.read)}</p>}
      {describeRead(npc.name, npc.read).map((line) => (
        <p key={line}>{line}</p>
      ))}
      {npc.read.lastSeen && (
        <p>
          Last seen {formatTime(npc.read.lastSeen.at).toLowerCase()}, at {state.locations[npc.read.lastSeen.where]?.name ?? 'somewhere'}.
        </p>
      )}

      {debts.length > 0 && (
        <>
          <h2>Between you</h2>
          <ul>
            {debts.map((f) => (
              <li key={f.id}>
                {f.kind === 'obligation' &&
                  (f.from === 'player' ? `You owe ${first} ${describeSubstance(f.substance)}` : `${first} owes you ${describeSubstance(f.substance)}`)}
                {f.kind === 'obligation' && f.deadline ? `, by ${formatTime(f.deadline).toLowerCase()}.` : '.'}
              </li>
            ))}
          </ul>
        </>
      )}

      {claims.length > 0 && (
        <>
          <h2>Things {first} told you</h2>
          <ul>
            {claims.map((f) => (
              <li key={f.id}>{f.kind === 'claim' && describeProposition(state, f.proposition)}</li>
            ))}
          </ul>
        </>
      )}

      {starters.length > 0 && onAct && (
        <>
          <h2>Or</h2>
          <div className="starters">
            {starters.map((s) => (
              <button key={s.label} type="button" className="choice quiet" onClick={() => onAct(s.action)}>
                {s.label}
              </button>
            ))}
          </div>
        </>
      )}

      {meetings.length > 0 && (
        <>
          <h2>Arranged</h2>
          <ul>
            {meetings.map((a) => (
              <li key={a.id}>
                {state.locations[a.place]?.name ?? a.place}, {formatTime(a.time).toLowerCase()}.
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}
